import { LoaderFunction, Params } from 'react-router-dom';

// Graphql
import { client } from '../../../../../gql/client';
import { FETCH_COMMUNICATIONS } from '../../../../../gql/queries/communication';
import { GET_SELECTORS_DATA, GET_CONCESSIONS } from '../../../../../gql/queries/orders/orders';
import { FETCH_PREDEFINED_ORDERS } from '../../../../../gql/queries/orders/predefinedData';
import { FETCH_USERS } from '../../../../../gql/queries/users';

// Types
import { AuthContextType } from '../../../../../hooks/useAuth';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

// Selectors
import { selectCommunicationOrders } from '../../../../../gql/selectors/communications';
import { store } from '../../../../../state/configureStore';
import { getCursor } from '../../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { refreshOrderFlags } from '../../../../../gql/services/orderFlags';

// Factoría: el loader global es `makeLoader()` sin args (byte-idéntico al de
// siempre); las pestañas de un LISTADO de módulo pasan `containerTypes` +
// el `tableId` de su cursor dedicado.
//
// `getContainerId` (resolver el container desde `params`) se quedó SIN
// llamadores al retirarse las pestañas del detalle de campaña. Se conserva
// la opción, que es lo que hace reutilizable esta factoría.
//
// Nuevo (pestañas de módulo del listado de Campañas) — `containerTypes` es
// ESTÁTICO (no depende de `params`): la pestaña "Comunicacions" del listado
// de Campañas filtra por TIPO de origen (`['campaign']`), no por un `:id`
// de la URL. Mismo criterio que `ActionsPage`/`MultimediaPage` (Fase 3 nav
// rework, RV).
export const makeLoader = (
  options: {
    getContainerId?: (params: Params) => string | undefined;
    tableId?: string;
    containerTypes?: string[];
  } = {}
): ((userData: AuthContextType | null) => LoaderFunction) => {
  const { getContainerId, tableId = tableIds.WORKORDER_COMMUNICATIONS, containerTypes } = options;
  return (userData) => async ({ params }) => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
  if (userData?.user) {
    const cursorCommunication = getCursor(tableId)(
      store.getState()
    );
    const roleActive = userData.user.user.rolActive;

    const dispatch = store.dispatch;

    // Fase 2 nav Trabajo/Revisión — containerId contextual (id de la Order
    // de la campaña), resincronizado en CADA run del loader.
    const containerId = getContainerId?.(params);
    if (containerId && cursorCommunication.containerId !== containerId) {
      dispatch(setCursor(tableId, { ...cursorCommunication, containerId }));
    }

    // containerTypes estático, resincronizado en CADA run del loader (mismo
    // criterio que containerId).
    if (
      containerTypes?.length &&
      JSON.stringify(cursorCommunication.containerTypes) !== JSON.stringify(containerTypes)
    ) {
      dispatch(setCursor(tableId, { ...cursorCommunication, containerTypes }));
    }

    // `containerTypes` estático (campaña) gana sobre el cursor; en la ruta
    // GLOBAL el valor sale del filtro "Origen" user-driven ya persistido en
    // `cursorCommunication` (leído ANTES de los dispatches de arriba).
    const effectiveContainerTypes = containerTypes?.length
      ? containerTypes
      : cursorCommunication.containerTypes;

    // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
    let effectiveConcessionId = cursorCommunication.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableId, { ...cursorCommunication, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido (HIT path no podía cerrarlo).
    dispatch(loadingData(tableId, false));

    const readCommunications = client.readQuery({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: cursorCommunication.take,
          cursor: cursorCommunication.cursor,
          orderBy: cursorCommunication.orderBy,
          sortField: cursorCommunication.sortField,
          roadId: cursorCommunication.roadId,
          concessionId: effectiveConcessionId,
          interlocutorGroupId: cursorCommunication.interlocutorGroupId,
          fromDate: cursorCommunication.fromDate,
          toDate: cursorCommunication.toDate,
          indexType: cursorCommunication.indexType,
          indexExpedition: cursorCommunication.indexExpedition,
          classification: cursorCommunication?.classification,
          states: cursorCommunication?.states,
          userIds: cursorCommunication?.userIds,
          // Fase 4 nav Trabajo/Revisión — filtro "Origen" (tabla GLOBAL,
          // user-driven vía cursor); ahora también acepta `containerTypes`
          // estático (pestaña "Comunicacions" del listado de Campañas).
          containerTypes: effectiveContainerTypes,
          teamIds: cursorCommunication?.teamIds,
          pkInitKm: cursorCommunication?.pkInitKm,
          pkInitMeter: cursorCommunication?.pkInitMeter,
          pkEndKm: cursorCommunication?.pkEndKm,
          pkEndMeter: cursorCommunication?.pkEndMeter,
          description: cursorCommunication?.description,
          predefinedOrderId: cursorCommunication?.predefinedOrderId,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: cursorCommunication?.onlyFlagged,
          // GOTCHA (review T1): omitir el campo en vez de null/undefined
          // explícito — un `containerId: null` SÍ entra en la keyArgs de
          // Apollo y partiría la caché histórica sin containerId.
          ...(containerId && { containerId }),
        },
      },
    });

    const totalCount = readCommunications?.communications?.totalCount;
    const pageInfo = readCommunications?.communications?.pageInfo;
    const isCacheEmpty = JSON.stringify(readCommunications) === '{}';
    const ordersWithDataCommunication =
      readCommunications &&
      !isCacheEmpty &&
      (await selectCommunicationOrders(
        readCommunications?.communications?.communications
      ));

    const [filtersData, predefinedOrdersResult, usersResult] = await Promise.all([
      client.query({
        query: GET_SELECTORS_DATA,
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const combinedData = {
      ordersWithDataCommunication,
      filtersData: {
        ...filtersData,
        data: {
          ...filtersData.data,
          predefinedOrders: predefinedOrdersResult.data?.predefinedOrders,
          users: usersResult.data?.users,
        },
      },
      totalCount,
      pageInfo,
      roleActive,
    };

    if (!readCommunications || isCacheEmpty) {
      dispatch(loadingData(tableId, true));
      try {
      let communications;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        const effectiveTake = firstPageTake(cursorCommunication.take, takeBase);
        communications = await client.query({
          query: FETCH_COMMUNICATIONS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              orderBy: cursorCommunication.orderBy ?? 'desc',
              sortField: cursorCommunication.sortField,
              roadId: cursorCommunication.roadId,
              concessionId: effectiveConcessionId,
              interlocutorGroupId: cursorCommunication.interlocutorGroupId,
              fromDate: cursorCommunication.fromDate,
              toDate: cursorCommunication.toDate,
              indexType: cursorCommunication.indexType,
              indexExpedition: cursorCommunication.indexExpedition,
              classification: cursorCommunication?.classification,
              states: cursorCommunication?.states,
              userIds: cursorCommunication?.userIds,
              containerTypes: effectiveContainerTypes,
              teamIds: cursorCommunication?.teamIds,
              pkInitKm: cursorCommunication?.pkInitKm,
              pkInitMeter: cursorCommunication?.pkInitMeter,
              pkEndKm: cursorCommunication?.pkEndKm,
              pkEndMeter: cursorCommunication?.pkEndMeter,
              description: cursorCommunication?.description,
              predefinedOrderId: cursorCommunication?.predefinedOrderId,
              onlyFlagged: cursorCommunication?.onlyFlagged,
              ...(containerId && { containerId }),
            },
          },
        });

        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          roadId: cursorCommunication.roadId,
          concessionId: effectiveConcessionId,
          interlocutorGroupId: cursorCommunication.interlocutorGroupId,
          fromDate: cursorCommunication.fromDate,
          toDate: cursorCommunication.toDate,
          indexType: cursorCommunication.indexType,
          indexExpedition: cursorCommunication.indexExpedition,
          classification: cursorCommunication?.classification,
          states: cursorCommunication?.states,
          userIds: cursorCommunication?.userIds,
          // Fase 4 nav Trabajo/Revisión — filtro "Origen" (tabla GLOBAL,
          // user-driven vía cursor); ahora también acepta `containerTypes`
          // estático (pestaña "Comunicacions" del listado de Campañas).
          containerTypes: effectiveContainerTypes,
          teamIds: cursorCommunication?.teamIds,
          pkInitKm: cursorCommunication?.pkInitKm,
          pkInitMeter: cursorCommunication?.pkInitMeter,
          pkEndKm: cursorCommunication?.pkEndKm,
          pkEndMeter: cursorCommunication?.pkEndMeter,
          description: cursorCommunication?.description,
          predefinedOrderId: cursorCommunication?.predefinedOrderId,
          onlyFlagged: cursorCommunication?.onlyFlagged,
          // Redux (no Apollo): full-replace de la slice del cursor — hace
          // falta el valor explícito para no perder el containerId
          // sincronizado al principio del loader.
          containerId,
        };

        dispatch(setCursor(tableId, setCursorData));
      } else {
        communications = await client.query({
          query: FETCH_COMMUNICATIONS,
          variables: {
            input: {
              take: cursorCommunication.take,
              cursor: cursorCommunication.cursor,
              orderBy: cursorCommunication.orderBy,
              sortField: cursorCommunication.sortField,
              roadId: cursorCommunication.roadId,
              concessionId: effectiveConcessionId,
              interlocutorGroupId: cursorCommunication.interlocutorGroupId,
              fromDate: cursorCommunication.fromDate,
              toDate: cursorCommunication.toDate,
              indexType: cursorCommunication.indexType,
              indexExpedition: cursorCommunication.indexExpedition,
              classification: cursorCommunication?.classification,
              states: cursorCommunication?.states,
              userIds: cursorCommunication?.userIds,
              containerTypes: effectiveContainerTypes,
              teamIds: cursorCommunication?.teamIds,
              pkInitKm: cursorCommunication?.pkInitKm,
              pkInitMeter: cursorCommunication?.pkInitMeter,
              pkEndKm: cursorCommunication?.pkEndKm,
              pkEndMeter: cursorCommunication?.pkEndMeter,
              description: cursorCommunication?.description,
              predefinedOrderId: cursorCommunication?.predefinedOrderId,
              onlyFlagged: cursorCommunication?.onlyFlagged,
              ...(containerId && { containerId }),
            },
          },
        });
      }
      const loading = communications.loading;
      const totalCount = communications.data.communications.totalCount;
      const pageInfo = communications.data.communications.pageInfo;
      const ordersWithDataCommunication = await selectCommunicationOrders(
        communications.data.communications.communications
      );

      const [filtersData, predefinedOrdersResult, usersResult] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_USERS,
          fetchPolicy: 'cache-first',
        }),
      ]);

      const combinedData = {
        ordersWithDataCommunication,
        filtersData: {
          ...filtersData,
          data: {
            ...filtersData.data,
            predefinedOrders: predefinedOrdersResult.data?.predefinedOrders,
            users: usersResult.data?.users,
          },
        },
        totalCount,
        pageInfo,
        loading,
        roleActive,
      };
      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque el bloque
        // MISS lance una excepción a mitad.
        dispatch(loadingData(tableId, false));
      }
    }

    return combinedData;
  }
  return {};
  } catch (err) {
    console.log('Loader offline fallback:', err);
    // FIX (loader-loading-flag-leak): defensa en profundidad para errores
    // antes del MISS branch.
    store.dispatch(loadingData(tableId, false));
    return { ordersWithDataCommunication: [], filtersData: { data: { concessions: [], roads: [], interlocutorGroups: [], mediaOfCommunications: [], predefinedOrders: [], users: [] } }, totalCount: 0, pageInfo: { hasNextPage: false, hasPreviousPage: false }, roleActive: userData?.user?.user?.rolActive };
  }
  };

  return { data: load() };
};
};

// Loader global (ruta `/work-orders/communications`) — comportamiento
// byte-idéntico al anterior a la Fase 2 nav Trabajo/Revisión.
export const LoaderCommunicationGeneralView = makeLoader();
